import { getPlatformMark } from "@mairko/brand";
import { ChevronDownIcon, GlobeIcon } from "lucide-react";
import {
	Source,
	Sources,
	SourcesContent,
	SourcesTrigger,
} from "@/components/ai-elements/sources";

export type SitesNode = {
	provider: "github" | "vercel" | "cloudflare" | "godaddy" | "wordpress";
	label: string;
	count: number;
	samples: string[];
	/** When count is 0: connected-but-empty vs not connected. */
	emptyKind?: "connected_empty" | "not_connected";
};

export type AdsCampaignLine = {
	name: string;
	budget: string;
	detail: string | null;
};

export type AdsScorecard = {
	title: string;
	caption: string;
	week: string | null;
	heading: string | null;
	campaigns: AdsCampaignLine[];
	pickOne: string[];
	portfolio: string[];
	extra: string | null;
};

export type ParsedHandoffSummary =
	| {
			kind: "sites";
			nodes: SitesNode[];
			notes: string[];
			asOf: string | null;
	  }
	| { kind: "ads"; card: AdsScorecard; asOf: string | null }
	| { kind: "plain"; text: string };

const PROVIDER_META: Record<
	SitesNode["provider"],
	{ label: string; markId: string; noun: string }
> = {
	github: { label: "GitHub", markId: "github", noun: "repos" },
	vercel: { label: "Vercel", markId: "vercel", noun: "projects" },
	cloudflare: { label: "Cloudflare", markId: "cloudflare", noun: "zones" },
	godaddy: { label: "GoDaddy", markId: "godaddy", noun: "domains" },
	wordpress: { label: "WordPress", markId: "wordpress", noun: "sites" },
};

const PROVIDER_RE =
	/(github|vercel|cloudflare|godaddy|wordpress)\s*[:·]\s*(?:connected\s*[·•\-–—]\s*)?(\d+)\s*(?:resources?|zones?|repos?|projects?|domains?|sites?)(?:\s*\(([^)]*)\))?/gi;

/**
 * Pull structured Sites inventory out of Rhea’s readiness digest.
 * Samples may be a short preview or a full portfolio list.
 */
function splitList(raw: string): string[] {
	return raw
		.split(",")
		.map((part) => part.replace(/\(\+\d+ more\)/g, "").trim())
		.filter((part) => part.length > 0);
}

function parseAdsScorecard(raw: string): AdsScorecard | null {
	const start = raw.search(/Ads check(?: for | · )/);
	if (start < 0) return null;
	const slice = raw
		.slice(start)
		.replace(/\s*·\s*decision=\S+/gi, "")
		.replace(/\s*·\s*approvalId=\S+/gi, "")
		.replace(/\s*·\s*asOf=\S+/gi, "")
		.trim();
	const lines = slice
		.split(/\n/)
		.map((line) => line.trim())
		.filter((line) => line.length > 0);
	const header = lines[0];
	if (!header) return null;

	const named = header.match(/^Ads check for (.+?) · (.+)$/);
	const which = /^Ads check · which account\?$/.test(header);
	if (!named && !which) return null;

	const title = named?.[1] ?? "Google Ads";
	const caption = named?.[2] ?? "Which account?";
	let week: string | null = null;
	let heading: string | null = null;
	let extra: string | null = null;
	const campaigns: AdsCampaignLine[] = [];
	let pickOne: string[] = [];
	let portfolio: string[] = [];

	for (const line of lines.slice(1)) {
		if (line.startsWith("This week:")) {
			week = line.replace(/^This week:\s*/, "");
			continue;
		}
		if (/^(On now|Paused) \(/.test(line)) {
			heading = line;
			continue;
		}
		if (line.startsWith("Pick one:")) {
			pickOne = splitList(line.slice("Pick one:".length));
			continue;
		}
		if (line.startsWith("Portfolio:")) {
			portfolio = splitList(line.slice("Portfolio:".length));
			continue;
		}
		if (line.startsWith("+") && /more/.test(line)) {
			extra = line.replace(/^[+·\s]+/, "");
			continue;
		}
		const bullet = line.match(/^•\s+(.+?)\s+—\s+(.+)$/);
		if (bullet?.[1] && bullet[2]) {
			const [budget, ...rest] = bullet[2].split(" · ");
			campaigns.push({
				name: bullet[1],
				budget: budget?.trim() ?? bullet[2],
				detail: rest.length > 0 ? rest.join(" · ") : null,
			});
		}
	}

	return {
		title,
		caption,
		week,
		heading,
		campaigns,
		pickOne,
		portfolio,
		extra,
	};
}

export function parseHandoffSummary(raw: string): ParsedHandoffSummary {
	const text = raw.trim();
	if (!text) return { kind: "plain", text: "" };

	const asOfMatch = text.match(/asOf=(\S+)/i);
	const asOf = asOfMatch?.[1] ?? null;
	const withoutAsOf = text.replace(/\s*asOf=\S+/gi, "").trim();
	const ads = parseAdsScorecard(withoutAsOf);
	if (ads) {
		return { kind: "ads", card: ads, asOf };
	}

	const nodes: SitesNode[] = [];
	const seen = new Set<string>();

	for (const match of withoutAsOf.matchAll(PROVIDER_RE)) {
		const provider = match[1]?.toLowerCase() as
			| SitesNode["provider"]
			| undefined;
		if (!provider || seen.has(provider)) continue;
		seen.add(provider);
		const count = Number(match[2] ?? 0);
		const samples = (match[3] ?? "")
			.split(",")
			.map((part) => part.replace(/…/g, "").trim())
			.filter((part) => part.length > 0 && part !== "…")
			.slice(0, 500);
		let emptyKind: SitesNode["emptyKind"];
		if (count === 0) {
			const slice = withoutAsOf.slice(
				Math.max(0, (match.index ?? 0) - 40),
				(match.index ?? 0) + (match[0]?.length ?? 0) + 160,
			);
			emptyKind = /not connected/i.test(slice)
				? "not_connected"
				: "connected_empty";
		}
		nodes.push({
			provider,
			label: PROVIDER_META[provider].label,
			count,
			samples,
			...(emptyKind ? { emptyKind } : {}),
		});
	}

	if (nodes.length === 0) {
		return { kind: "plain", text: withoutAsOf };
	}

	/** Named lookup handoffs list matches as • owner/repo — surface as chips. */
	const bulletIds = [
		...withoutAsOf.matchAll(/•\s*([A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+)/g),
	]
		.map((m) => m[1])
		.filter((id): id is string => Boolean(id));
	if (bulletIds.length > 0) {
		for (const node of nodes) {
			if (node.provider === "github" && node.samples.length === 0) {
				node.samples = [...new Set(bulletIds)].slice(0, 12);
			}
		}
	}

	const notes = withoutAsOf
		.split(/\s*[—–]\s*/)
		.map((part) => part.trim())
		.filter((part) => {
			if (part.length < 16) return false;
			if (/^sites (readiness|inventory)/i.test(part)) return false;
			if (/^latest saved check|^fresh check/i.test(part)) return false;
			if (/do not invent/i.test(part)) return false;
			if (/^full \w+ list/i.test(part)) return false;
			if (/^complete enabled/i.test(part)) return false;
			if (/^siteUrl=/i.test(part)) return false;
			if (/^•\s/.test(part)) return false;
			if (/^found \d+ match/i.test(part)) return false;
			if (/^best match:/i.test(part)) return false;
			if (
				/(github|vercel|cloudflare|godaddy|wordpress)\s*[:·]\s*(?:connected\s*[·•\-–—]\s*)?\d+\s*(?:resources?|zones?|repos?|projects?|domains?|sites?)/i.test(
					part,
				)
			) {
				return false;
			}
			return true;
		})
		.slice(0, 4);

	return { kind: "sites", nodes, notes, asOf };
}

type SitesCardProps = {
	nodes: SitesNode[];
	notes: string[];
	asOf: string | null;
};

function resourceHref(
	provider: SitesNode["provider"],
	name: string,
): string | undefined {
	switch (provider) {
		case "godaddy":
		case "cloudflare":
			return `https://${name.replace(/^https?:\/\//, "")}`;
		case "wordpress":
			return name.startsWith("http")
				? name
				: `https://${name.replace(/^https?:\/\//, "")}`;
		case "github":
			return name.includes("/")
				? `https://github.com/${name}`
				: undefined;
		case "vercel":
			return undefined;
		default: {
			const _exhaustive: never = provider;
			return _exhaustive;
		}
	}
}

function PortfolioSources({
	node,
	defaultOpen,
}: {
	node: SitesNode;
	defaultOpen: boolean;
}) {
	const meta = PROVIDER_META[node.provider];
	const noun =
		node.samples.length === 1
			? meta.noun.replace(/s$/, "")
			: meta.noun;

	return (
		<Sources defaultOpen={defaultOpen} className="mc-sites-sources group">
			<SourcesTrigger
				count={node.samples.length}
				className="mc-sites-sources-trigger"
			>
				<p className="font-medium">
					{node.samples.length.toLocaleString()} {noun}
				</p>
				<ChevronDownIcon className="size-3.5 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
			</SourcesTrigger>
			<SourcesContent className="mc-sites-sources-content">
				{node.samples.map((sample) => {
					const href = resourceHref(node.provider, sample);
					if (href) {
						return (
							<Source key={sample} href={href} title={sample}>
								<GlobeIcon className="size-3.5 shrink-0 opacity-60" />
								<span className="min-w-0 truncate font-medium">
									{sample}
								</span>
							</Source>
						);
					}
					return (
						<span key={sample} className="mc-sites-sources-plain">
							{sample}
						</span>
					);
				})}
			</SourcesContent>
		</Sources>
	);
}

/** Branded portfolio card — AI Elements Sources for expandable inventories. */
export function SitesInventoryCard({ nodes, notes, asOf }: SitesCardProps) {
	const focused = nodes.length === 1;
	const fullList =
		focused &&
		nodes[0] !== undefined &&
		nodes[0].samples.length > 3 &&
		nodes[0].samples.length >= Math.min(nodes[0].count, 4);

	return (
		<div className="mc-sites-card">
			<div className="mc-sites-card-intro">
				<p className="mc-sites-card-title">
					{focused ? nodes[0]!.label : "Sites connectors"}
				</p>
				<p className="mc-sites-card-caption">
					{fullList
						? `Full list · ${nodes[0]!.count.toLocaleString()} ${PROVIDER_META[nodes[0]!.provider].noun}`
						: focused
							? `${nodes[0]!.count.toLocaleString()} in portfolio`
							: "Connected portfolio by connector"}
				</p>
			</div>

			<ul className="mc-sites-card-list">
				{nodes.map((node) => {
					const mark = getPlatformMark(
						PROVIDER_META[node.provider].markId,
						18,
					);
					const expandable =
						node.samples.length > 3 ||
						(node.samples.length === node.count &&
							node.samples.length > 1);
					/**
					 * Long portfolios stay collapsed (tap “69 domains”).
					 * TODO(you): tweak this threshold if ≤8 open-by-default
					 * feels too chatty or too shy for your workflows.
					 */
					const openByDefault = node.samples.length <= 8;
					return (
						<li key={node.provider} className="mc-sites-card-row">
							<span className="mc-sites-card-row-head">
								{mark ? (
									<span
										className="integration-mark mc-sites-card-mark"
										style={{ color: mark.color }}
										dangerouslySetInnerHTML={{ __html: mark.svg }}
									/>
								) : null}
								<span className="mc-sites-card-row-label">{node.label}</span>
								<span className="mc-sites-card-row-count">
									{node.count.toLocaleString()}
								</span>
							</span>
							{node.samples.length > 0 ? (
								expandable ? (
									<PortfolioSources
										node={node}
										defaultOpen={openByDefault}
									/>
								) : (
									<span className="mc-sites-card-chips">
										{node.samples.map((sample) => (
											<span
												key={sample}
												className="mc-sites-card-chip"
												title={sample}
											>
												{sample}
											</span>
										))}
										{node.count > node.samples.length ? (
											<span className="mc-sites-card-more">
												+{node.count - node.samples.length} more — ask for
												the full list
											</span>
										) : null}
									</span>
								)
							) : node.count > 0 ? (
								<span className="mc-sites-card-empty">
									{node.count.toLocaleString()}{" "}
									{PROVIDER_META[node.provider].noun} · no preview samples
								</span>
							) : (
								<span className="mc-sites-card-empty">
									{node.emptyKind === "not_connected"
										? "Not connected"
										: "Connected · portfolio empty"}
								</span>
							)}
						</li>
					);
				})}
			</ul>

			{notes.length > 0 ? (
				<ul className="mc-sites-card-notes">
					{notes.map((note) => (
						<li key={note}>{note}</li>
					))}
				</ul>
			) : null}

			{asOf ? (
				<p className="mc-sites-card-asof">
					Checked{" "}
					{new Date(asOf).toLocaleString(undefined, {
						month: "short",
						day: "numeric",
						hour: "numeric",
						minute: "2-digit",
					})}
				</p>
			) : null}
		</div>
	);
}

/** @deprecated Use SitesInventoryCard */
export const SitesInventoryWeb = SitesInventoryCard;

type AdsCardProps = {
	card: AdsScorecard;
	asOf: string | null;
};

/** Plain-language Ads briefing — spend, results, and what’s on. */
export function AdsScorecardCard({ card, asOf }: AdsCardProps) {
	const mark = getPlatformMark("ads", 18);
	const emptyPick = card.pickOne.length > 0 || card.portfolio.length > 0;

	return (
		<div className="mc-ads-card">
			<div className="mc-ads-card-intro">
				<span className="mc-ads-card-kicker">
					{mark ? (
						<span
							className="integration-mark mc-sites-card-mark"
							style={{ color: mark.color }}
							dangerouslySetInnerHTML={{ __html: mark.svg }}
						/>
					) : null}
					Google Ads
				</span>
				<p className="mc-ads-card-title">{card.title}</p>
				<p className="mc-ads-card-caption">{card.caption}</p>
			</div>

			{card.week ? <p className="mc-ads-card-week">{card.week}</p> : null}

			{card.heading ? (
				<p className="mc-ads-card-heading">{card.heading}</p>
			) : null}

			{card.campaigns.length > 0 ? (
				<ul className="mc-ads-card-list">
					{card.campaigns.map((row) => (
						<li key={`${row.name}-${row.budget}`} className="mc-ads-card-row">
							<p className="mc-ads-card-row-name">{row.name}</p>
							<p className="mc-ads-card-row-budget">
								Set to spend up to {row.budget}
							</p>
							{row.detail ? (
								<p className="mc-ads-card-row-detail">{row.detail}</p>
							) : null}
						</li>
					))}
				</ul>
			) : null}

			{emptyPick ? (
				<div className="mc-ads-card-pick">
					<p className="mc-ads-card-pick-label">
						{card.pickOne.length > 0
							? "A few accounts match — say which one"
							: "Accounts Miles can see"}
					</p>
					<span className="mc-sites-card-chips">
						{(card.pickOne.length > 0
							? card.pickOne
							: card.portfolio
						).map((name) => (
							<span key={name} className="mc-sites-card-chip" title={name}>
								{name}
							</span>
						))}
					</span>
				</div>
			) : null}

			{card.campaigns.length > 0 ? (
				<p className="mc-ads-card-hint">
					Results are the action the ads are meant to get — a form, a
					call, a booking. “Each” is what one of those cost this week.
				</p>
			) : null}

			{card.extra ? <p className="mc-ads-card-extra">{card.extra}</p> : null}

			{asOf ? (
				<p className="mc-sites-card-asof">
					Checked{" "}
					{new Date(asOf).toLocaleString(undefined, {
						month: "short",
						day: "numeric",
						hour: "numeric",
						minute: "2-digit",
					})}
				</p>
			) : null}
		</div>
	);
}
